/**
 * Identifies which company this build serves.
 *
 * Khufu resolves every request to a company from the `X-Tenant-Key` header and rejects
 * anything it cannot place, so a build without this value cannot talk to the API at all
 * — including the login and token-verification endpoints, which are not public routes.
 *
 * The value is inlined at build time, which is why each company gets its own deploy.
 * Adding or changing a tenant means a rebuild, not an environment edit.
 */
export const TENANT_KEY = process.env.REACT_APP_TENANT_KEY;

if (!TENANT_KEY) {
  throw new Error(
    'REACT_APP_TENANT_KEY is not set. Every request must identify a company, so there is no safe default. ' +
      'Set it to a key listed in the TENANTS variable in Khufu, for example REACT_APP_TENANT_KEY=khufu.'
  );
}

/**
 * Display name for this company. Cosmetic only — it names the browser tab and labels the
 * sidebar logo, so it falls back to the tenant key rather than refusing to start.
 */
export const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || TENANT_KEY;

/**
 * Scopes a browser-storage key to this company.
 *
 * Each company has its own origin today, so this is not what keeps their data apart —
 * the backend does that. It removes one-origin-per-tenant as a hard requirement, so
 * path-based routing or a shared preview domain cannot silently mix two companies, and
 * it lets a developer run both tenants against localhost without clearing storage in
 * between, which is how the isolation checks are meant to be run.
 */
export const storageKey = (name: string) => `${name}:${TENANT_KEY}`;

export const TOKEN_KEY = storageKey('token');
export const CART_KEY = storageKey('cart');
export const PREPARED_ORDER_KEY = storageKey('preparedOrder');
export const PERSIST_KEY = storageKey('warehouse');

/**
 * Moves storage written under the old unscoped keys onto the scoped ones.
 *
 * Without this, shipping the namespacing would sign everyone out on its own — separately
 * from, and earlier than, the single sign-out the tenant claim already forces.
 *
 * Both keys have to move together. Migrating the token alone achieves nothing, because
 * the signed-in user lives in the persisted Redux state — leaving that behind sends the
 * user to the login screen regardless, which is the outcome this exists to avoid.
 *
 * It runs at module scope rather than from a caller because `store.ts` imports this file,
 * and redux-persist starts reading storage as that module is evaluated. Anything invoked
 * later — including from `index.tsx` — would run after the read it is meant to precede.
 *
 * Safe to delete once a release carrying it has been out long enough for sessions to turn
 * over.
 */
const migrateLegacyStorage = () => {
  // redux-persist prefixes every key it owns; see KEY_PREFIX in its constants.
  const moves: [string, string][] = [
    ['token', TOKEN_KEY],
    ['persist:warehouse', `persist:${PERSIST_KEY}`]
  ];

  moves.forEach(([from, to]) => {
    const value = localStorage.getItem(from);

    if (value === null) {
      return;
    }

    if (localStorage.getItem(to) === null) {
      localStorage.setItem(to, value);
    }

    localStorage.removeItem(from);
  });
};

migrateLegacyStorage();
